"use client";

import React, { useCallback, useMemo } from "react";
import {
  ResponsiveCommandSelect,
  type SelectOption,
} from "@/components/ui/responsive-select";

/**
 * Pagination metadata passed to the shell. Shell derives `hasMore` /
 * `isLoadingMore` from these flags so each caller doesn't repeat the logic.
 */
export interface EntitySelectShellPageInfo {
  page: number;
  totalPages: number;
  firstPage: number;
  isBackgroundFetching: boolean;
  onLoadMore: () => void;
}

export interface EntitySelectShellProps {
  /** Selected value passed through to `ResponsiveCommandSelect`. */
  value: string;
  /** Called when a non-sentinel value is chosen. */
  onValueChange: (value: string, option?: SelectOption) => void;
  /** Options to render. Callers append sentinels like "__ADD_NEW__" themselves. */
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;

  /**
   * Internal debounced search value owned by the caller. `searchValue` from
   * caller props takes precedence if provided (e.g. controlled search).
   */
  searchValue: string;
  /** Caller-controlled search value override (passes through). */
  externalSearchValue?: string;
  /** Setter for the internal debounced search value. */
  setSearchValue: (val: string) => void;
  /** Optional caller passthrough for search-change side effects. */
  onSearchChange?: (val: string) => void;

  isLoading: boolean;
  recentKey: string;

  highZIndex?: boolean;
  /**
   * Pass `undefined` to use the underlying select's default, `true`/`false` to
   * explicitly opt in or out.
   */
  centerOnScreen?: boolean;
  expandInline?: boolean;
  expandInlineVariant?: "replace" | "floating";
  onOpenChange?: (open: boolean) => void;
  title?: string;
  description?: string;
  searchPlaceholder?: string;
  customOptionRenderer?: (option: SelectOption) => React.ReactNode;

  pageInfo: EntitySelectShellPageInfo;
  /** When true, pagination is disabled (e.g. while FTS search is active). */
  disablePagination?: boolean;

  /**
   * Optional sentinel value handler. When `value === addNewSentinel`, the
   * shell calls `onAddNew()` instead of `onValueChange`. Used by callers that
   * append an `__ADD_NEW__` option.
   */
  addNewSentinel?: string;
  onAddNew?: () => void;

  containerClassName?: string;
  className?: string;
}

/**
 * Internal shell that owns the `ResponsiveCommandSelect` rendering plus the
 * pagination-flag derivation and search-change wrapper. Behavior is identical
 * to the patterns previously inlined in each `*SelectWithModal` component.
 */
export function EntitySelectShell({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  searchValue,
  externalSearchValue,
  setSearchValue,
  onSearchChange,
  isLoading,
  recentKey,
  highZIndex,
  centerOnScreen,
  expandInline = true,
  expandInlineVariant = "floating",
  onOpenChange,
  title,
  description,
  searchPlaceholder,
  customOptionRenderer,
  pageInfo,
  disablePagination,
  addNewSentinel,
  onAddNew,
  containerClassName = "w-full",
  className = "w-full",
}: EntitySelectShellProps) {
  const { page, totalPages, firstPage, isBackgroundFetching, onLoadMore } =
    pageInfo;

  // Last addressable page differs between zero-based (0..totalPages-1) and
  // one-based (1..totalPages) callers. `hasMore` is true while there is at
  // least one un-loaded page beyond the current one.
  const lastPage = firstPage === 0 ? totalPages - 1 : totalPages;
  const hasMore = !disablePagination && page < lastPage;

  const isLoadingMore =
    !!isBackgroundFetching && page > firstPage && !disablePagination;

  const handleLoadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) onLoadMore();
  }, [hasMore, isLoadingMore, onLoadMore]);

  const handleSearchChange = useCallback(
    (val: string) => {
      setSearchValue(val);
      onSearchChange?.(val);
    },
    [setSearchValue, onSearchChange]
  );

  const handleValueChange = useCallback(
    (val: string, option?: SelectOption) => {
      if (addNewSentinel && val === addNewSentinel) {
        onAddNew?.();
        return;
      }
      onValueChange(val, option);
    },
    [addNewSentinel, onAddNew, onValueChange]
  );

  const effectiveSearchValue = useMemo(
    () => externalSearchValue ?? searchValue,
    [externalSearchValue, searchValue]
  );

  return (
    <div className={containerClassName}>
      <ResponsiveCommandSelect
        value={value}
        onValueChange={handleValueChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled}
        searchValue={effectiveSearchValue}
        onSearchChange={handleSearchChange}
        isSearching={isLoading}
        className={className}
        enableRecents
        recentKey={recentKey}
        recentMax={8}
        highZIndex={highZIndex}
        searchPlaceholder={searchPlaceholder}
        title={title}
        description={description}
        onLoadMore={handleLoadMore}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        customOptionRenderer={customOptionRenderer}
        centerOnScreen={centerOnScreen}
        expandInline={expandInline}
        expandInlineVariant={expandInlineVariant}
        onOpenChange={onOpenChange}
      />
    </div>
  );
}
